import { ModalOpenButton } from "@msys/ui";
import { Send as SendIcon } from "@mui/icons-material";
import { Button } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { namedOperations } from "../../../clients/graphqlTypes.js";
import { useFeature } from "../../../common/FeatureFlags.js";
import { RestrictedByCapabilityWithDebug } from "../../auth/RestrictedByCapability.js";
import { RestrictedByDocumentPermissionWithDebug } from "../../auth/RestrictedByDocumentPermission.js";
import { RestrictedByProjectPermissionWithDebug } from "../../auth/RestrictedByProjectPermission.js";
import { useUserData } from "../../auth/useUserData.js";
import { CreateQuoteFromRequirementButton } from "../quotes/buttons/CreateQuoteForRequirementButton.js";
import { FinalizeDecisionsInBulkButton } from "../quotes/buttons/FinalizeDecisionsInBulkButton.js";
import { RequestCreateButton } from "../requests/buttons/RequestCreateButton.js";
import { RequestQuoteForRequirementButton } from "../requests/buttons/RequestQuoteForRequirementButton.js";
import { RequestViewButton } from "../requests/buttons/RequestViewButton.js";
import {
  RequirementAction_ProjectFragment,
  RequirementAction_RequirementFragment,
} from "./RequirementAction.generated.js";
import { RequirementSendViaEmailModal } from "./RequirementSendViaEmailModal.js";

interface Props {
  doc: RequirementAction_RequirementFragment;
  project: RequirementAction_ProjectFragment;
  pathToProject: string;
  startDecisionsInBulkProcess?(): void;
}

export const RequirementAction = ({
  doc: requirement,
  project,
  pathToProject,
  startDecisionsInBulkProcess,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslate(["Global"]);
  const viewer = useUserData().currentUser!;

  const actionButtons: React.ReactNode[] = [
    <RestrictedByProjectPermissionWithDebug
      permission="SHARE_REQUIREMENTS"
      project={project}
      key={"send-requirement-via-email"}
    >
      <ModalOpenButton
        Modal={RequirementSendViaEmailModal}
        modalProps={{
          project,
          requirement,
        }}
      >
        <Button color="primary" variant="contained" endIcon={<SendIcon />}>
          {t("Send", { ns: "Global" })}
        </Button>
      </ModalOpenButton>
    </RestrictedByProjectPermissionWithDebug>,
    <RestrictedByDocumentPermissionWithDebug
      permission="MANAGE_REQUIREMENTS"
      document={requirement}
      key={"view requirement request"}
    >
      {requirement.owningSystemOrganisationId === viewer.organisation.id &&
        requirement.outgoingRequests.length > 0 && (
          <RequestViewButton projectId={project.id} requirement={requirement} />
        )}
    </RestrictedByDocumentPermissionWithDebug>,
    <RestrictedByProjectPermissionWithDebug
      permission="SHARE_REQUIREMENTS"
      project={project}
      key={"create requirement request"}
    >
      {requirement.owningSystemOrganisationId === viewer.organisation.id &&
        !requirement.requirementRequestWonId && (
          <RequestQuoteForRequirementButton
            requirementId={requirement.id}
            projectId={project.id}
            refetchQueries={[
              namedOperations.Query.ProjectOverviewIncomingQuotes,
              namedOperations.Query.ProjectQuotes,
              namedOperations.Query.ProjectRequests,
            ]}
            Button={<RequestCreateButton type="button" />}
          />
        )}
    </RestrictedByProjectPermissionWithDebug>,

    <RestrictedByCapabilityWithDebug
      capability="QUOTING"
      key={"import requirements to quote"}
    >
      <RestrictedByProjectPermissionWithDebug
        permission="MANAGE_QUOTES"
        project={project}
      >
        <CreateQuoteFromRequirementButton
          docId={requirement.id}
          docProjectId={project.id}
          pathToProject={pathToProject}
        />
      </RestrictedByProjectPermissionWithDebug>
    </RestrictedByCapabilityWithDebug>,
    startDecisionsInBulkProcess && (
      <RestrictedByDocumentPermissionWithDebug
        permission="TAKE_REQUIREMENTS_DECISION"
        document={requirement}
        key={"finalize-decisions"}
      >
        <FinalizeDecisionsInBulkButton onClick={startDecisionsInBulkProcess} />
      </RestrictedByDocumentPermissionWithDebug>
    ),
  ].filter(Boolean);

  return actionButtons;
};
